import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LADO_DA_FOTO_PX, type PerfilDaUsuaria } from '@reducee/contracts';
import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';

import { chamarApi } from '../api/cliente.ts';
import { ROTULO_PAPEL, useSessao } from '../autenticacao/sessao.tsx';
import { Avatar, enderecoDaMinhaFoto } from '../componentes/Avatar.tsx';
import { CampoSenha, CampoTexto } from '../componentes/Campo.tsx';
import { RecusaDaApi } from '../componentes/RecusaDaApi.tsx';
import { Botao, CabecalhoDePagina, Cartao } from '../componentes/Superficie.tsx';
import { ler } from '../formulario.ts';

/**
 * Meu perfil: o que a usuária logada troca sozinha.
 *
 * Papel e e-mail de login aparecem, mas não se editam aqui. O e-mail de login
 * fica com a Administradora porque o sistema não confirma e-mail: um erro de
 * digitação da própria usuária a trancaria do lado de fora, sem ninguém que
 * pudesse abrir.
 */
export function PaginaPerfil(): React.JSX.Element {
  const consulta = useQuery({
    queryKey: ['perfil'],
    queryFn: () => chamarApi<PerfilDaUsuaria>('/perfil'),
  });
  const perfil = consulta.data;

  return (
    <section>
      <CabecalhoDePagina
        area="Conta"
        titulo="Meu perfil"
        descricao="Foto, nome, canais de lembrete e senha de quem está logada."
      />

      {/* Duas colunas que não se alinham por linha: numa grade comum, o cartão
        * da Foto esticava até a altura dos Dados e virava um bloco vazio. No
        * celular a ordem é a da leitura: Foto, Dados, Senha. */}
      {perfil ? (
        <div className="grid items-start gap-4 lg:grid-cols-2">
          <div className="grid min-w-0 gap-4">
            <FotoDoPerfil perfil={perfil} />
            <DadosDoPerfil perfil={perfil} />
          </div>
          <SenhaDoPerfil />
        </div>
      ) : consulta.isLoading ? (
        <p className="text-texto-suave">Carregando…</p>
      ) : (
        <RecusaDaApi erro={consulta.error} padrao="Não foi possível carregar o perfil." />
      )}
    </section>
  );
}

/** Falha do lado do navegador, antes de a foto sair: a mensagem já é a certa. */
class FotoIlegivel extends Error {}

function FotoDoPerfil({ perfil }: { perfil: PerfilDaUsuaria }): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const entrada = useRef<HTMLInputElement>(null);

  const enviar = useMutation({
    mutationFn: async (arquivo: File) => {
      const formulario = new FormData();
      formulario.append('foto', await reduzirFoto(arquivo), 'foto');
      return chamarApi<PerfilDaUsuaria>('/perfil/foto', { metodo: 'PUT', corpo: formulario });
    },
    onSuccess: (atualizado) => clienteDeConsultas.setQueryData(['perfil'], atualizado),
  });

  const remover = useMutation({
    mutationFn: () => chamarApi<PerfilDaUsuaria>('/perfil/foto', { metodo: 'DELETE' }),
    onSuccess: (atualizado) => clienteDeConsultas.setQueryData(['perfil'], atualizado),
  });

  const ocupado = enviar.isPending || remover.isPending;
  const erro = enviar.error ?? remover.error;

  function aoEscolher(evento: ChangeEvent<HTMLInputElement>): void {
    const arquivo = evento.target.files?.[0];
    // Limpar deixa escolher o mesmo arquivo de novo, depois de uma recusa.
    evento.target.value = '';
    if (!arquivo) return;
    remover.reset();
    enviar.mutate(arquivo);
  }

  return (
    <Cartao
      titulo="Foto"
      descricao="Aparece no menu lateral. A imagem é recortada em quadrado, a partir do centro."
    >
      <div className="flex flex-wrap items-center gap-4">
        <Avatar
          nome={perfil.nome}
          foto={enderecoDaMinhaFoto(perfil.versaoDaFoto)}
          tamanho={96}
          descricao={perfil.versaoDaFoto ? 'Sua foto' : `Sem foto; iniciais de ${perfil.nome}`}
        />
        <div className="flex flex-wrap gap-2">
          <input
            ref={entrada}
            type="file"
            accept="image/*"
            data-teste="arquivo-da-foto"
            onChange={aoEscolher}
            className="hidden"
          />
          <Botao
            variante="primario"
            teste="escolher-foto"
            desabilitado={ocupado}
            onClick={() => entrada.current?.click()}
          >
            {perfil.versaoDaFoto ? 'Trocar foto' : 'Enviar foto'}
          </Botao>
          {perfil.versaoDaFoto ? (
            <Botao
              teste="remover-foto"
              desabilitado={ocupado}
              onClick={() => {
                enviar.reset();
                remover.mutate();
              }}
            >
              Remover foto
            </Botao>
          ) : null}
        </div>
      </div>

      {ocupado ? (
        <p role="status" className="mt-3 text-sm text-texto-suave">
          {enviar.isPending ? 'Enviando a foto…' : 'Removendo a foto…'}
        </p>
      ) : null}
      <RecusaDaApi
        erro={erro}
        padrao={erro instanceof FotoIlegivel ? erro.message : 'Não foi possível salvar a foto.'}
      />
    </Cartao>
  );
}

function DadosDoPerfil({ perfil }: { perfil: PerfilDaUsuaria }): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const { recarregar } = useSessao();
  const [salvo, definirSalvo] = useState(false);

  const salvar = useMutation({
    mutationFn: (dados: {
      nome: string;
      emailNotificacao: string | null;
      whatsappNotificacao: string | null;
    }) => chamarApi<PerfilDaUsuaria>('/perfil', { metodo: 'PATCH', corpo: dados }),
    onSuccess: async (atualizado) => {
      clienteDeConsultas.setQueryData(['perfil'], atualizado);
      // O nome do menu vem da sessão, e a API acabou de reemitir o token.
      await recarregar();
      definirSalvo(true);
    },
  });

  function aoEnviar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    definirSalvo(false);
    const campos = new FormData(evento.currentTarget);
    salvar.mutate({
      nome: String(campos.get('nome') ?? '').trim(),
      emailNotificacao: ler(campos, 'emailNotificacao'),
      whatsappNotificacao: ler(campos, 'whatsappNotificacao'),
    });
  }

  return (
    <Cartao titulo="Dados" descricao="O nome é o que aparece no menu e nas telas da operação.">
      <form onSubmit={aoEnviar} data-teste="formulario-do-perfil" className="grid gap-3.5">
        <CampoTexto
          rotulo="Nome"
          name="nome"
          defaultValue={perfil.nome}
          required
          minLength={2}
          maxLength={160}
          autoComplete="name"
        />

        {/* Texto, e não campo travado: um campo que não aceita digitação
          * parece quebrado. O que ela pode fazer está dito logo abaixo. */}
        <div className="rounded-[11px] border border-linha bg-[#f8fafb] px-3.25 py-3">
          <dl className="grid gap-3 text-xs sm:grid-cols-2">
            <div className="min-w-0">
              <dt className="text-[11px] font-extrabold text-[#536575]">E-mail de login</dt>
              <dd data-teste="email-de-login" className="mt-1 break-all text-marinho">
                {perfil.email}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] font-extrabold text-[#536575]">Papel</dt>
              <dd className="mt-1 text-marinho">{ROTULO_PAPEL[perfil.papel]}</dd>
            </div>
          </dl>
          <p className="mt-2 text-[11px] leading-snug text-texto-suave">
            E-mail de login e papel são trocados pela Administradora, em Configurações.
          </p>
        </div>

        <CampoTexto
          rotulo="E-mail para lembretes"
          name="emailNotificacao"
          type="email"
          defaultValue={perfil.emailNotificacao ?? ''}
          autoComplete="email"
          dica="Pode ser outro que não o de login. Em branco, não recebe lembrete por e-mail."
        />
        <CampoTexto
          rotulo="WhatsApp para lembretes"
          name="whatsappNotificacao"
          type="tel"
          inputMode="tel"
          defaultValue={perfil.whatsappNotificacao ?? ''}
          maxLength={24}
          placeholder="5571999999999"
          autoComplete="tel"
        />

        <div>
          <Botao tipo="submit" variante="primario" teste="salvar-perfil" desabilitado={salvar.isPending}>
            Salvar dados
          </Botao>
        </div>
        {salvo ? (
          <p role="status" className="text-sm text-sucesso">
            Dados salvos.
          </p>
        ) : null}
        <RecusaDaApi erro={salvar.error} padrao="Não foi possível salvar os dados." />
      </form>
    </Cartao>
  );
}

function SenhaDoPerfil(): React.JSX.Element {
  const formulario = useRef<HTMLFormElement>(null);
  const [divergente, definirDivergente] = useState(false);
  const [trocada, definirTrocada] = useState(false);

  const trocar = useMutation({
    mutationFn: (dados: { senhaAtual: string; novaSenha: string }) =>
      chamarApi<void>('/perfil/senha', { metodo: 'POST', corpo: dados }),
    onSuccess: () => {
      formulario.current?.reset();
      definirTrocada(true);
    },
  });

  function aoEnviar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    definirTrocada(false);
    const campos = new FormData(evento.currentTarget);
    const novaSenha = String(campos.get('novaSenha') ?? '');

    // Conferida aqui porque a confirmação não vai à API: ela só existe para
    // pegar o erro de digitação antes que ele vire a senha.
    const confere = novaSenha === String(campos.get('confirmacao') ?? '');
    definirDivergente(!confere);
    if (!confere) return;

    trocar.mutate({ senhaAtual: String(campos.get('senhaAtual') ?? ''), novaSenha });
  }

  return (
    <Cartao
      titulo="Senha"
      descricao="Ao trocar, as outras sessões abertas com a senha antiga são encerradas. Esta continua."
    >
      <form
        ref={formulario}
        onSubmit={aoEnviar}
        data-teste="formulario-da-senha"
        className="grid gap-3.5"
      >
        <CampoSenha
          rotulo="Senha atual"
          name="senhaAtual"
          required
          autoComplete="current-password"
        />
        <CampoSenha
          rotulo="Nova senha"
          name="novaSenha"
          required
          minLength={10}
          maxLength={200}
          autoComplete="new-password"
          dica="Ao menos 10 caracteres."
        />
        <CampoSenha
          rotulo="Repita a nova senha"
          name="confirmacao"
          required
          minLength={10}
          maxLength={200}
          autoComplete="new-password"
        />

        <div>
          <Botao tipo="submit" variante="primario" teste="trocar-senha" desabilitado={trocar.isPending}>
            Trocar senha
          </Botao>
        </div>
        {divergente ? (
          <p role="alert" className="rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
            A nova senha e a repetição não são iguais.
          </p>
        ) : null}
        {trocada ? (
          <p role="status" className="text-sm text-sucesso">
            Senha trocada.
          </p>
        ) : null}
        <RecusaDaApi erro={trocar.error} padrao="Não foi possível trocar a senha." />
      </form>
    </Cartao>
  );
}

/**
 * Recorta em quadrado pelo centro e reduz para o lado do contrato.
 *
 * A redução é no navegador para a foto de 12 MB do celular não atravessar a
 * rede nem ocupar a memória da API. O fundo branco vem antes porque o JPEG,
 * que é o plano B, não tem transparência e pintaria de preto o que era vazio.
 */
async function reduzirFoto(arquivo: File): Promise<Blob> {
  let imagem: ImageBitmap;
  try {
    imagem = await createImageBitmap(arquivo);
  } catch {
    throw new FotoIlegivel('Não foi possível ler esta imagem. Envie uma foto em JPEG, PNG ou WebP.');
  }

  const lado = Math.min(imagem.width, imagem.height);
  const tela = document.createElement('canvas');
  tela.width = LADO_DA_FOTO_PX;
  tela.height = LADO_DA_FOTO_PX;

  const contexto = tela.getContext('2d');
  if (!contexto) {
    imagem.close();
    throw new FotoIlegivel('Este navegador não conseguiu preparar a foto.');
  }

  contexto.fillStyle = '#ffffff';
  contexto.fillRect(0, 0, LADO_DA_FOTO_PX, LADO_DA_FOTO_PX);
  contexto.imageSmoothingQuality = 'high';
  contexto.drawImage(
    imagem,
    (imagem.width - lado) / 2,
    (imagem.height - lado) / 2,
    lado,
    lado,
    0,
    0,
    LADO_DA_FOTO_PX,
    LADO_DA_FOTO_PX,
  );
  imagem.close();

  // O Safari não gera WebP pelo canvas e entrega PNG no lugar, que sai bem
  // maior. Quando isso acontece, JPEG.
  const webp = await paraArquivo(tela, 'image/webp', 0.85);
  if (webp?.type === 'image/webp') return webp;

  const jpeg = await paraArquivo(tela, 'image/jpeg', 0.88);
  if (!jpeg) throw new FotoIlegivel('Este navegador não conseguiu preparar a foto.');
  return jpeg;
}

function paraArquivo(tela: HTMLCanvasElement, tipo: string, qualidade: number): Promise<Blob | null> {
  return new Promise((resolver) => tela.toBlob(resolver, tipo, qualidade));
}
